import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import CustomerShell from "@/components/CustomerShell";
import InstallerShell from "@/components/installer/InstallerShell";
import OperationsShell from "@/components/operations/OperationsShell";
import { IdleWarningModal } from "@/components/IdleWarningModal";

import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Journey from "@/pages/Journey";
import Messages from "@/pages/Messages";
import Documents from "@/pages/Documents";
import Appointments from "@/pages/Appointments";
import MySystem from "@/pages/MySystem";
import Installer from "@/pages/Installer";
import InstallerProjects from "@/pages/InstallerProjects";
import InstallerProject from "@/pages/InstallerProject";
import EngineerAdmin from "@/pages/EngineerAdmin";
import InstallerCalendar from "@/pages/InstallerCalendar";
import InstallerUpcomingVisits from "@/pages/InstallerUpcomingVisits";
import InstallerTasks from "@/pages/InstallerTasks";
import InstallerTodo from "@/pages/installer/InstallerTodo";
import OperationsTasks from "@/pages/operations/OperationsTasks";
import InstallerSurveys from "@/pages/InstallerSurveys";
import InstallerSurveyCapture from "@/pages/InstallerSurveyCapture";
import NotificationSettings from "@/pages/NotificationSettings";
import Profile from "@/pages/Profile";
import About from "@/pages/About";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import AdminSecurity from "@/pages/AdminSecurity";
import OperationsDashboard from "@/pages/operations/OperationsDashboard";
import OperationsAdministration from "@/pages/operations/OperationsAdministration";import OperationsProjects from "@/pages/operations/OperationsProjects";
import OperationsReports from "@/pages/operations/OperationsReports";
import OperationsAttentionCentre from "@/pages/operations/OperationsAttentionCentre";
import OperationsNotificationCentre from "@/pages/operations/OperationsNotificationCentre";
import OperationsEngineers from "@/pages/operations/OperationsEngineers";
import OperationsFieldTeam from "@/pages/operations/OperationsFieldTeam";
import OperationsBlackouts from "@/pages/operations/OperationsBlackouts";
import OperationsScheduling from "@/pages/operations/OperationsScheduling";
import OperationsSystemSettings from "@/pages/operations/OperationsSystemSettings";
import OperationsCustomers from "@/pages/operations/OperationsCustomers";
import OperationsCustomerDetail from "@/pages/operations/OperationsCustomerDetail";
import OperationsDocuments from "@/pages/operations/OperationsDocuments";
import OperationsDocumentDetail from "@/pages/operations/OperationsDocumentDetail";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import SalesTasks from "@/pages/sales/SalesTasks";
import SalesTodo from "@/pages/sales/SalesTodo";
import EngineersRouteGuard from "@/pages/EngineersRouteGuard";
import CustomerActivate from "@/pages/CustomerActivate";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Licences from "@/pages/legal/Licences";

/**
 * AutoShell wraps a route in the appropriate portal chrome for the signed-in
 * user — CustomerShell for customers, InstallerShell for Field Users,
 * OperationsShell for Operations. Signed-out visitors see the plain page.
 *
 * FIELD-001C · Operations shared routes (Profile, About, Messages,
 * Documents, Appointments, MySystem, legal pages) MUST render inside
 * the OperationsShell so the sidebar/nav destinations, header and
 * Back behaviour match the caller's actual portal — a browser Back,
 * refresh or direct `/profile` load must never briefly show the
 * Field Portal chrome for an Ops user, and clicking a nav link from
 * the shared page must go to `/operations/*`, not `/installer/*`.
 * While auth is still bootstrapping (`loading=true`), render the
 * page bare rather than committing to a shell — prevents a flash of
 * the wrong portal on direct URL loads.
 */
function AutoShell({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div data-testid="auto-shell-loading">{children}</div>;
    if (user?.role === "customer") {
        return <CustomerShell>{children}</CustomerShell>;
    }
    // SALES-ROLE-001A · Sales role reuses the Operations shell chrome
    // for shared pages (profile, messages, documents, appointments,
    // legal). The shell's NAV filter (see OperationsShell.jsx) hides
    // administration + attention/notification/documents/resource
    // sections that Sales must not access.
    if (user?.role === "operations" || user?.role === "sales") {
        return <OperationsShell>{children}</OperationsShell>;
    }
    if (user?.role === "installer") {
        return <InstallerShell>{children}</InstallerShell>;
    }
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <div className="App">
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            style: { background: "#131B2F", color: "#fff", border: "1px solid #1F2A47", fontFamily: "Inter" },
                        }}
                    />
                    <IdleWarningModal />
                    <RouteErrorBoundary>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Navigate to="/" replace />} />
                        <Route path="/register" element={<Navigate to="/" replace />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/activate" element={<CustomerActivate />} />

                        <Route path="/settings/change-password" element={
                            <ProtectedRoute><ChangePassword /></ProtectedRoute>
                        } />
                        <Route path="/settings/security" element={
                            <ProtectedRoute permission="manage_users"><AdminSecurity /></ProtectedRoute>
                        } />

                        <Route path="/dashboard" element={
                            <ProtectedRoute role="customer"><CustomerShell><Dashboard /></CustomerShell></ProtectedRoute>
                        } />
                        <Route path="/journey" element={
                            <ProtectedRoute role="customer"><CustomerShell><Journey /></CustomerShell></ProtectedRoute>
                        } />

                        <Route path="/messages" element={
                            <ProtectedRoute><AutoShell><Messages /></AutoShell></ProtectedRoute>
                        } />
                        <Route path="/documents" element={
                            <ProtectedRoute><AutoShell><Documents /></AutoShell></ProtectedRoute>
                        } />
                        <Route path="/appointments" element={
                            <ProtectedRoute><AutoShell><Appointments /></AutoShell></ProtectedRoute>
                        } />
                        <Route path="/system" element={
                            <ProtectedRoute><AutoShell><MySystem /></AutoShell></ProtectedRoute>
                        } />
                        <Route path="/profile" element={
                            <ProtectedRoute><AutoShell><Profile /></AutoShell></ProtectedRoute>
                        } />
                        <Route path="/about" element={
                            <ProtectedRoute><AutoShell><About /></AutoShell></ProtectedRoute>
                        } />

                        {/* Legal pages — public so unauthenticated users can read them,
                            wrapped in AutoShell so signed-in customers still see the sidebar. */}
                        <Route path="/legal/privacy"  element={<AutoShell><Privacy /></AutoShell>} />
                        <Route path="/legal/terms"    element={<AutoShell><Terms /></AutoShell>} />
                        <Route path="/legal/licences" element={<AutoShell><Licences /></AutoShell>} />

                        <Route path="/installer" element={<ProtectedRoute role="installer"><Installer /></ProtectedRoute>} />
                        <Route path="/installer/projects" element={<ProtectedRoute role="installer"><InstallerProjects /></ProtectedRoute>} />
                        <Route path="/installer/project/:id" element={<ProtectedRoute role="installer"><InstallerProject /></ProtectedRoute>} />
                        <Route path="/installer/calendar" element={<ProtectedRoute role="installer"><InstallerCalendar /></ProtectedRoute>} />
                        <Route path="/installer/upcoming-visits" element={<ProtectedRoute role="installer"><InstallerUpcomingVisits /></ProtectedRoute>} />
                        <Route path="/installer/tasks" element={<ProtectedRoute role="installer"><InstallerTasks /></ProtectedRoute>} />
                        <Route path="/installer/todo" element={<ProtectedRoute role="installer"><InstallerTodo /></ProtectedRoute>} />
                        <Route path="/installer/surveys" element={<ProtectedRoute role="installer"><InstallerSurveys /></ProtectedRoute>} />
                        <Route path="/installer/surveys/:projectId" element={<ProtectedRoute role="installer"><InstallerSurveyCapture /></ProtectedRoute>} />
                        <Route path="/settings/notifications" element={<ProtectedRoute roles={["installer","operations"]} permission="manage_system_settings"><NotificationSettings /></ProtectedRoute>} />
                        <Route path="/settings/engineers" element={<ProtectedRoute roles={["installer","operations"]}><EngineersRouteGuard /></ProtectedRoute>} />

                        {/* Operations role (DEV-001) — SALES-ROLE-001A/B:
                            Sales lands on /sales (dedicated dashboard).
                            Sales is still admitted to the shared read
                            surfaces (customer/project/calendar/tasks/
                            reports). All Administration + engineer /
                            attention / notification / documents /
                            blackouts / identity audit / field team
                            surfaces remain Ops-only. */}
                        <Route path="/sales" element={
                            <ProtectedRoute roles={["sales","operations"]}><SalesDashboard /></ProtectedRoute>
                        } />
                        <Route path="/operations" element={<ProtectedRoute role="operations"><OperationsDashboard /></ProtectedRoute>} />
                        <Route path="/operations/attention" element={<ProtectedRoute role="operations"><OperationsAttentionCentre /></ProtectedRoute>} />
                        <Route path="/operations/notifications" element={<ProtectedRoute role="operations"><OperationsNotificationCentre /></ProtectedRoute>} />
                        <Route path="/operations/customers" element={
                            <ProtectedRoute roles={["operations","sales"]}><OperationsCustomers /></ProtectedRoute>
                        } />
                        <Route path="/operations/customers/:customerId" element={
                            <ProtectedRoute roles={["operations","sales"]}><OperationsCustomerDetail /></ProtectedRoute>
                        } />
                        <Route path="/operations/projects" element={
                            <ProtectedRoute roles={["operations","sales"]}><OperationsProjects /></ProtectedRoute>
                        } />
                        {/* DEV-020 · Universal Single Project Focus View for
                            Operations + Sales. Reuses `OperationsProjectDetail`
                            without the master-detail list chrome. Sales role
                            is admitted here for parity with the existing
                            `/operations/projects` visibility. */}
                        <Route path="/operations/projects/:projectId" element={
                            <ProtectedRoute roles={["operations","sales"]}>
                                {React.createElement(require("@/pages/operations/OperationsProjectFocus").default)}
                            </ProtectedRoute>
                        } />
                        <Route path="/sales/projects/:projectId" element={
                            <ProtectedRoute roles={["sales","operations"]}>
                                {React.createElement(require("@/pages/operations/OperationsProjectFocus").default)}
                            </ProtectedRoute>
                        } />
                        <Route path="/operations/calendar" element={
                            <ProtectedRoute roles={["operations","sales"]}>
                                <InstallerCalendar shell="operations" />
                            </ProtectedRoute>
                        } />
                        {/* FIELD-001C · Operations task list surface.
                            Reuses <InstallerTasks/> inside <OperationsShell/>
                            so Workload counters can drill into
                            /operations/tasks?engineer_id=... without the
                            role="installer" guard redirecting Ops users. */}
                        <Route path="/operations/tasks" element={
                            <ProtectedRoute roles={["operations","sales"]}><OperationsTasks /></ProtectedRoute>
                        } />
                        {/* DEV-019 UAT correction 4 · Canonical Sales
                            Tasks route. Reuses <InstallerTasks/> so
                            filters/scopes/permissions align exactly
                            with /operations/tasks; the Sales caller
                            is scoped server-side to Sales-visible
                            projects + personally-assigned tasks. */}
                        <Route path="/sales/tasks" element={
                            <ProtectedRoute roles={["sales","operations"]}><SalesTasks /></ProtectedRoute>
                        } />
                        {/* DEV-019G · Sales To-do workspace — the day-to-day
                            action list backed by the existing
                            ``projects.sales_actions[]`` sub-array. Kept
                            deliberately distinct from `/sales/tasks`,
                            which represents FORMAL project/workflow tasks. */}
                        <Route path="/sales/todo" element={
                            <ProtectedRoute roles={["sales","operations"]}><SalesTodo /></ProtectedRoute>
                        } />
                        <Route path="/operations/engineers" element={
                            <ProtectedRoute role="operations"><OperationsEngineers /></ProtectedRoute>
                        } />
                        {/* FIELD-001C1 · Resource Management is the new
                            operational-only surface. `/operations/engineers`
                            remains as a compatibility alias; the redirect
                            for `?tab=administration` is handled inside
                            OperationsEngineers.jsx. */}
                        <Route path="/operations/resources" element={
                            <ProtectedRoute role="operations"><OperationsEngineers /></ProtectedRoute>
                        } />
                        <Route path="/operations/administration/field-team" element={
                            <ProtectedRoute role="operations"><OperationsFieldTeam /></ProtectedRoute>
                        } />
                        <Route path="/operations/administration/identity-audit" element={
                            <ProtectedRoute role="operations">
                                {React.createElement(require("@/pages/operations/OperationsIdentityAudit").default)}
                            </ProtectedRoute>
                        } />
                        <Route path="/operations/administration/blackouts" element={
                            <ProtectedRoute role="operations"><OperationsBlackouts /></ProtectedRoute>
                        } />
                        {/* DEV-024 · Administration → Scheduling sub-hub.
                            Groups Resource working hours (deep-links to
                            Field Team) and Blackout periods under one
                            Administration entry. Role-gated to Operations
                            like the other administration surfaces; per-
                            card permission checks are performed inside
                            the page itself. */}
                        <Route path="/operations/administration/scheduling" element={
                            <ProtectedRoute role="operations"><OperationsScheduling /></ProtectedRoute>
                        } />
                        {/* DEV-023B (final MVP) · Administration →
                            System Settings hub. Reuses the existing
                            Data Management + Run Identity Audit
                            surfaces so the Administration overview
                            no longer surfaces Notifications-as-System-
                            Settings. Role-gated to Operations; per-
                            card permission gating happens inside. */}
                        <Route path="/operations/administration/system-settings" element={
                            <ProtectedRoute role="operations"><OperationsSystemSettings /></ProtectedRoute>
                        } />
                        <Route path="/operations/documents" element={
                            <ProtectedRoute role="operations"><OperationsDocuments /></ProtectedRoute>
                        } />
                        <Route path="/operations/documents/:docId" element={
                            <ProtectedRoute role="operations"><OperationsDocumentDetail /></ProtectedRoute>
                        } />
                        <Route path="/operations/reports" element={
                            <ProtectedRoute roles={["operations","sales"]}><OperationsReports /></ProtectedRoute>
                        } />
                        <Route path="/operations/administration" element={
                            <ProtectedRoute role="operations"><OperationsAdministration /></ProtectedRoute>
                        } />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </RouteErrorBoundary>
                </div>
            </BrowserRouter>
        </AuthProvider>
    );
}
